import useSWR from 'swr'
import { Info } from 'lucide-react'
import { Text } from '@design-system/components/Text'
import { cn } from '@/lib/utils'
import { CreditIcon } from '@/components/icons/CreditIcon'
import type { CreditInfo, SubscriptionTier } from '@/lib/api'
import { usersApi } from '@/lib/api'
import { isTrialLike, isFairgenInternalTier } from '@/lib/billing/tier'
import { planTierDisplayName } from './displayNames'
import { OverageInfoTooltip } from './OverageInfoTooltip'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useTranslation } from 'react-i18next'
import { intlLocaleForUi } from '@/i18n'

interface PlanSummaryCardProps {
  tier: SubscriptionTier
  creditInfo: CreditInfo | null | undefined
  /** When true, balance/creditLimit reflect the org-level shared pool (Enterprise). */
  isPoolBilling?: boolean
  daysUntilReset: number
  renewsAt?: string | null
  trialEndsAt?: string | null
  endsAt?: string | null
  lsStatus?: string | null
  /** Per-credit overage rate in cents at the user's current plan/interval. */
  overageRateCents: number
}

export function PlanSummaryCard(props: PlanSummaryCardProps) {
  const { tier, creditInfo, isPoolBilling = false, daysUntilReset, renewsAt, trialEndsAt, endsAt, lsStatus, overageRateCents } = props
  const { t, i18n } = useTranslation('app')
  const numberLocale = intlLocaleForUi(i18n.language)

  const { data: bonusData } = useSWR('me/credits/bonus', () => usersApi.getBonusCredits(), {
    revalidateOnFocus: false,
  })
  const bonusBalance = bonusData?.balance ?? 0
  const isInternal = isFairgenInternalTier(tier)
  const used = creditInfo?.used ?? 0
  const limit = creditInfo?.creditLimit ?? 0
  const overageUsed = creditInfo?.overageUsed ?? 0
  // Source of truth — backend already encodes "did this user exceed their plan?".
  const isOver = !isInternal && (creditInfo?.isInOverage ?? false)

  const pct = isInternal ? 0 : limit > 0 ? Math.round((used / limit) * 100) : 0
  // Visual width caps at 100% — overflow is communicated by the small dot
  // at the end of the track and the "over plan" text.
  const fillPct = Math.min(pct, 100)
  const balance = Math.max(0, limit - used)

  // Credit cycle is always 30 days regardless of billing interval (annual
  // subs pay yearly but credits refresh monthly). `daysUntilReset` is the
  // source of truth — computed from `billing_cycle_anchor + cycleDays`.
  // Trial-like tiers show "ends in X days" from `trialEndsAt` instead.
  // Cancelled subs show "access ends in X days" from `endsAt`.
  const resetCopy = (() => {
    if (isTrialLike(tier) && trialEndsAt) {
      const trialDate = new Date(trialEndsAt)
      if (!Number.isNaN(trialDate.getTime())) {
        const days = Math.max(
          0,
          Math.ceil((trialDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
        )
        return days === 0 ? t('billing.finished') : t('billing.endsIn', { count: days })
      }
    }
    if (lsStatus === 'cancelled' && endsAt) {
      const endsDate = new Date(endsAt)
      if (!Number.isNaN(endsDate.getTime())) {
        const days = Math.max(
          0,
          Math.ceil((endsDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
        )
        return t('billing.accessEndsIn', { count: days })
      }
    }
    return t('billing.resetsIn', { count: daysUntilReset })
  })()

  return (
    <div
      data-testid="billing-plan-summary"
      className="flex h-full flex-col gap-4 rounded-xl bg-muted/40 p-5"
    >
      {lsStatus && lsStatus !== 'active' && (
        <div className={cn(
          'w-fit rounded-full px-2.5 py-1 text-xs font-medium leading-none',
          lsStatus === 'cancelled' && 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
          lsStatus === 'past_due' && 'bg-destructive/15 text-destructive',
          lsStatus === 'paused' && 'bg-muted text-muted-foreground',
        )}>
          {lsStatus === 'cancelled' ? t('billing.cancelled') : lsStatus === 'past_due' ? t('billing.pastDue') : lsStatus}
        </div>
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <Text size="sm" color="muted" className="flex items-center gap-1">
            <CreditIcon className="h-3.5 w-3.5" />
            {isPoolBilling ? t('billing.teamCreditPool') : t('billing.creditsThisCycle')}
          </Text>
          <p className="text-sm leading-snug text-muted-foreground">
            <span className="font-medium text-foreground">
              {t('billing.planLine', { plan: planTierDisplayName(tier) })}
            </span>{' '}
            · {resetCopy}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <span
            data-testid="billing-plan-summary-pct"
            className="rounded-full border border-border bg-muted px-2.5 py-1 text-xs font-medium leading-none text-foreground"
          >
            {t('billing.pctUsed', { pct })}
          </span>
          {isOver ? (
            <OverageInfoTooltip
              overageCredits={overageUsed}
              rateCents={overageRateCents}
              side="top"
              triggerTestId="billing-plan-summary-overage-info"
            />
          ) : null}
        </div>
      </div>

      <div className="mt-auto flex flex-col gap-2">
        {isInternal ? (
          <div className="flex items-end justify-end">
            <span className="font-display text-3xl leading-none text-foreground">
              {t('billing.unlimited')}
            </span>
          </div>
        ) : (
          <>
            <div className="flex items-end justify-between">
              <div className="flex items-center gap-1.5">
                <Text size="xs" color="muted" className="dark:text-white/60">
                  {isOver
                    ? t('billing.overPlan', { count: overageUsed.toLocaleString(numberLocale) })
                    : t('billing.remaining', { count: balance.toLocaleString(numberLocale) })}
                </Text>
                {!isOver && bonusBalance > 0 && (
                  <>
                    <Text size="xs" color="muted" className="dark:text-white/60">·</Text>
                    <Text size="xs" className="text-amber-600 dark:text-amber-400 font-medium">
                      {t('billing.bonus', { count: bonusBalance.toLocaleString(numberLocale) })}
                    </Text>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3 w-3 cursor-default text-muted-foreground hover:text-foreground transition-colors" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[220px] text-center">
                        {t('billing.bonusTooltip')}
                      </TooltipContent>
                    </Tooltip>
                  </>
                )}
              </div>
              <div className="flex items-baseline gap-1">
                <span className="font-display text-3xl leading-none tabular-nums text-foreground">
                  {used.toLocaleString(numberLocale)}
                </span>
                <span className="font-display text-sm leading-none text-muted-foreground">
                  {t('billing.ofLimit', { count: limit.toLocaleString(numberLocale) })}
                </span>
              </div>
            </div>

            <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all duration-500"
                style={{ width: `${fillPct}%` }}
              />
              {isOver ? (
                <span
                  aria-hidden
                  className="absolute right-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-primary ring-2 ring-card"
                />
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
